/**
 * SearchLogsPage — Jun 2026
 * Analytics + live log over `asb606_admin.site_search_logs` (written by the customer platform).
 * Route: /control/cloudgm/search-logs
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, RefreshCw, Search, Filter, Info } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { Input } from '../components/ui/input';
import { KpiTile, RankList, HourlyBars, LoadingBlock } from '../components/search-logs/SearchLogsWidgets';
import { SearchLogsTable } from '../components/search-logs/SearchLogsTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [7, 30, 90, 180];

export default function SearchLogsPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [onlyZero, setOnlyZero] = useState(false);
  const [onlyCustomers, setOnlyCustomers] = useState(false);
  const [country, setCountry] = useState('');
  const [device, setDevice] = useState('');
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState(null);
  const limit = 50;

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/search-logs/summary`, { params: { days }, headers: getAuthHeaders() });
      setSummary(r.data?.data || null);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }, [days]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/search-logs`, {
        params: { days, page, limit, q: q || undefined, only_zero: onlyZero || undefined, only_customers: onlyCustomers || undefined,
                  country: country || undefined, device: device || undefined },
        headers: getAuthHeaders(),
      });
      setRows(r.data?.data || []); setTotal(r.data?.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [days, page, q, onlyZero, onlyCustomers, country, device]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { setPage(1); }, [days, q, onlyZero, onlyCustomers, country, device]);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/search-logs/export.csv`, { params: { days }, headers: getAuthHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: `site-search-logs-${days}d.csv` });
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const showFields = async () => {
    try {
      const r = await axios.get(`${API}/admin/search-logs/fields`, { headers: getAuthHeaders() });
      setFields(r.data?.data || null);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const pct = (n) => (summary?.total ? `${Math.round((n / summary.total) * 100)}%` : '');

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <DashboardHeader showBackButton backPath="/control/cloudgm" />
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
            { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
            { label_ar: 'سجلات محركات البحث', label_en: 'Search Engine Logs', path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="search-logs-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{t('سجلات محركات البحث', 'Search Engine Logs')}</h1>
            <p className="text-sm text-white/50 mt-1 max-w-2xl">
              {t('كل عملية بحث يقوم بها عميل أو زائر في منصة العملاء تُسجَّل هنا للدراسة: ماذا يبحثون، ما الذي لا يجدونه، ومن أين.',
                 'Every search by a customer or guest on the customer platform lands here for study: what they look for, what they fail to find, and from where.')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-white/10 overflow-hidden" data-testid="search-period">
              {PERIODS.map((p) => (
                <button key={p} onClick={() => setDays(p)} className={`px-3 py-1.5 text-xs ${days === p ? 'bg-[#d4af37] text-black font-bold' : 'text-white/60 hover:bg-white/5'}`} data-testid={`search-period-${p}`}>
                  {t(`${p} يوم`, `${p}d`)}
                </button>
              ))}
            </div>
            <button onClick={() => { loadSummary(); loadRows(); }} className="h-8 w-8 rounded-lg bg-white/5 border border-white/10 text-white/70 flex items-center justify-center" data-testid="search-refresh"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={exportCsv} className="h-8 px-3 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs flex items-center gap-1" data-testid="search-export-csv"><Download className="w-3.5 h-3.5" />CSV</button>
            <button onClick={showFields} className="h-8 w-8 rounded-lg bg-white/5 border border-white/10 text-white/50 flex items-center justify-center" title={t('الحقول المكتشفة', 'Detected fields')} data-testid="search-fields-btn"><Info className="w-4 h-4" /></button>
          </div>
        </div>

        {fields && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-[11px] text-white/60" dir="ltr" data-testid="search-fields-panel">
            <b className="text-white/80">{fields.collection}</b> · {fields.documents} docs · detected: {JSON.stringify(fields.detected)}
            <div className="mt-1 text-white/40">keys: {fields.all_keys.join(', ') || '—'}</div>
          </div>
        )}

        {!summary ? <LoadingBlock /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiTile label={t('عمليات البحث', 'Searches')} value={summary.total} hint={summary.capped ? t('آخر 20,000 فقط', 'last 20,000 only') : ''} testid="kpi-total" />
              <KpiTile label={t('عملاء مسجّلون', 'Unique customers')} value={summary.unique_customers} accent="text-emerald-300" testid="kpi-customers" />
              <KpiTile label={t('بحث زوّار', 'Guest searches')} value={summary.guest_searches} hint={pct(summary.guest_searches)} testid="kpi-guests" />
              <KpiTile label={t('بدون نتائج', 'No results')} value={summary.zero_results} hint={pct(summary.zero_results)} accent="text-red-300" testid="kpi-zero" />
              <KpiTile label={t('فتح نتيجة', 'Result clicks')} value={summary.clicks} accent="text-sky-300" testid="kpi-clicks" />
              <KpiTile label={t('المساعد الذكي', 'AI assists')} value={summary.ai_assists} accent="text-[#d4af37]" testid="kpi-ai" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <RankList title={t('الأكثر بحثاً', 'Most searched')} items={summary.top_queries} emptyText={t('لا بيانات', 'No data')} onPick={setQ} accent="bg-[#d4af37]/70" testid="rank-top"
                        renderExtra={(i) => (i.zero ? ` · ${i.zero}✕` : '')} />
              <RankList title={t('بحث بدون نتائج (فرص جديدة)', 'No-result searches (opportunities)')} items={summary.zero_result_queries} emptyText={t('لا بيانات', 'No data')} onPick={setQ} accent="bg-red-500/60" testid="rank-zero" />
              <HourlyBars title={t('توزيع الساعات', 'Hour of day')} hours={summary.hourly} t={t} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <RankList title={t('الدول', 'Countries')} items={summary.countries} emptyText={t('لا بيانات', 'No data')} onPick={setCountry} accent="bg-emerald-500/60" testid="rank-countries" />
              <RankList title={t('المدن', 'Cities')} items={summary.cities} emptyText={t('لا بيانات', 'No data')} accent="bg-emerald-500/40" testid="rank-cities" />
              <RankList title={t('الأجهزة', 'Devices')} items={summary.devices} emptyText={t('لا بيانات', 'No data')} onPick={setDevice} accent="bg-sky-500/60" testid="rank-devices" />
              <RankList title={t('الصفحات / الأقسام', 'Pages / sections')} items={[...summary.pages, ...summary.sections.map((s) => ({ ...s, key: `§ ${s.key}` }))]} emptyText={t('لا بيانات', 'No data')} accent="bg-violet-500/60" testid="rank-pages" />
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-2" data-testid="search-filters">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute top-2.5 start-3 text-white/30" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ابحث في كلمات البحث أو رقم العميل…', 'Filter by query or customer id…')} className="bg-white/5 border-white/10 ps-9" data-testid="search-filter-q" />
          </div>
          <Toggle on={onlyZero} set={setOnlyZero} label={t('بدون نتائج فقط', 'No results only')} testid="search-filter-zero" />
          <Toggle on={onlyCustomers} set={setOnlyCustomers} label={t('عملاء مسجّلون فقط', 'Customers only')} testid="search-filter-customers" />
          {(country || device || q) && (
            <button onClick={() => { setCountry(''); setDevice(''); setQ(''); }} className="text-xs text-white/50 hover:text-white flex items-center gap-1" data-testid="search-filter-clear">
              <Filter className="w-3 h-3" />{t('مسح الفلاتر', 'Clear')}{country ? ` · ${country}` : ''}{device ? ` · ${device}` : ''}
            </button>
          )}
        </div>

        {loading ? <LoadingBlock /> : <SearchLogsTable rows={rows} t={t} page={page} limit={limit} total={total} onPage={setPage} />}
      </main>
    </div>
  );
}

function Toggle({ on, set, label, testid }) {
  return (
    <button type="button" onClick={() => set(!on)} data-testid={testid}
            className={`px-3 py-1.5 rounded-lg text-xs border ${on ? 'bg-[#d4af37]/15 border-[#d4af37]/50 text-[#d4af37]' : 'border-white/10 text-white/60 hover:bg-white/5'}`}>
      {label}
    </button>
  );
}
